/**
 * Tebligat taleplerinin UYAP portalıyla aynı kurulması için ortak kurallar (DEV-3519).
 *
 * Kaynak: UYAP avukat portalının tebligat formu (main.js, 23.09.2026) ve portalın kendi
 * gönderimi (TDT-3528 eki avukat.uyap.gov.tr.har, getIcraTebligatTalebiAdresSorgula,
 * getIcraTalepEvrakHazirla ve avukatIcraTalepEvrakiGonder kayıtları).
 *
 * Bu dosya "@/main"i import etmez; kurallar birim testlerinde doğrudan sınanıyor.
 */
import {DEVRE_KESICI_MESAJI, kotaDoluHatasiMi} from "../BaglantiKurtarma";
import {KIMLIK_UYUSMAZLIGI_MESAJI} from "../OturumKimligi";
import {UYAP_ENGEL_KAPISI_MESAJI, uyapEngelHatasiMi, uyapEngelMetniMi} from "../UyapEngeli";

/** getPortalAvukatTalepTebligatTuruList_brd.ajx cevabındaki bir satır. */
export interface TebligatTuruSatiri {
    masrafAdi: string,
    fiyati: number,
    postaMasraflariId: number
}

export interface TebligatTalepAlani {
    id: string,
    title: string
}

/**
 * Tebligat türünü, UYAP'ın gönderim anında bu avukata verdiği listeden bulur.
 *
 * Tür adı ve ücret aynı satırdan alınmalı. MDB'deki sabit tablo kullanılamaz: UYAP aynı
 * tür numarasına avukattan avukata farklı ad ve ücret verebiliyor. DEV-3519'da 2 numara
 * Adana'daki avukat için "T.K.21/2 Şerhli(Dönüşlü)" 390 TL'ydi, İstanbul'daki avukat için
 * ise hâlâ "T.K.21/2 Şerhli" adıyla geliyordu. Sabit 390 TL gönderilince UYAP talebi
 * PRTL_GNL_100012 ile reddetti. Bu yüzden türü listede bulamazsak MDB'ye dönmüyoruz; ödeme
 * ve imzadan önce hata veriyoruz.
 */
export function canliTebligatTuruBul(liste: TebligatTuruSatiri[] | null | undefined, tebligatTuru: number | string | null | undefined): TebligatTuruSatiri {
    // Boş değer 0'a (E-Tebligat) eşlenmesin: "" == 0 doğru olduğu için tür seçilmemiş bir
    // talep E-Tebligat ücretiyle gidiyordu.
    const turNo = (tebligatTuru === null || tebligatTuru === undefined || String(tebligatTuru).trim() === "")
        ? NaN
        : Number(tebligatTuru);
    if (!isFinite(turNo)) {
        throw new Error("Talepte tebligat türü seçilmemiş. Tebligat türünü seçerek talebi yeniden oluşturunuz.");
    }
    const satir = Array.isArray(liste)
        ? liste.find((tur) => Number(tur.postaMasraflariId) === turNo)
        : undefined;
    if (!satir) {
        throw new Error("Seçilen tebligat türü (" + tebligatTuru + ") UYAP'ın güncel tebligat türü listesinde bulunamadı. " +
            "UYAP türü kaldırmış ya da değiştirmiş olabilir; tebligat türünü yeniden seçerek talebi tekrar oluşturunuz.");
    }
    // null ve "" Number() ile 0'a dönüyor; ücreti okunamayan satır 0 TL sayılmamalı.
    const hamFiyat: unknown = satir.fiyati;
    const fiyati = (hamFiyat === null || hamFiyat === undefined || String(hamFiyat).trim() === "")
        ? NaN
        : Number(hamFiyat);
    if (!isFinite(fiyati) || fiyati < 0) {
        throw new Error("UYAP'ın tebligat türü listesinde \"" + satir.masrafAdi + "\" için ücret okunamadı.");
    }
    return {masrafAdi: satir.masrafAdi, fiyati: fiyati, postaMasraflariId: satir.postaMasraflariId};
}

/** Portalın adresTuru değerinden getIcraTebligatTalebiAdresSorgula'ya giden kod. */
export const TEBLIGAT_ADRES_KODU: { [adresTuru: number]: string } = {
    0: "ADRTR00013", // E-Tebligat Adresi
    1: "ADRTR00008", // Mernis Adresi
    2: "ADRTR00011"  // Mersis Adresi
};

export const MERNIS_ADRES_KODU = TEBLIGAT_ADRES_KODU[1];

/**
 * 3 (Yeni Adres) kullanıcıdan gelir ve sorgulanmaz. 4 (Vekilin E-Tebligat Adresi) portalda
 * seçilen vekilin kisiKurumId'siyle ADRTR00015 olarak sorgulanıyor. Toplu akışta vekil seçimi
 * olmadığı için sorgulanmıyor; görevdeki adres gönderiliyor.
 */
export function tebligatAdresKodu(adresTuru: number | string | null | undefined): string | null {
    if (adresTuru === null || adresTuru === undefined || String(adresTuru).trim() === "")
        return null;
    return TEBLIGAT_ADRES_KODU[Number(adresTuru)] || null;
}

export interface TebligatAdresSorguCevabi {
    adres?: string,
    ilAdi?: string,
    ilceAdi?: string
}

/**
 * Adres sorgusu cevabından talebin `adres` alanını portal gibi üretir. Kayıt yoksa null döner.
 *
 * - Mernis: adres avukata gösterilmez, cevapta boş gelir. Kaydın varlığını ilAdi gösterir.
 *   Portal talebe yalnızca "VAR" yazar.
 * - Diğer türler: portal "adres il / ilçe" yazar; eksik parçalar boş kalır.
 *
 * Portal kayıt yoksa "Adres kaydı bulunamadı!" uyarısı verip talebi eklemiyor. UYAP bu
 * durumda errorCode dönmüyor, adres ve ilAdi boş bir 200 cevabı veriyor (TDT-3528 HAR,
 * 21.09.2026).
 */
export function tebligatAdresDegeri(cevap: TebligatAdresSorguCevabi | null | undefined, adresKodu: string): string | null {
    if (!cevap)
        return null;
    const adres = typeof cevap.adres == "string" ? cevap.adres : "";
    if (adresKodu === MERNIS_ADRES_KODU) {
        const kayitVar = adres.trim().length > 0 || (typeof cevap.ilAdi == "string" && cevap.ilAdi != "");
        return kayitVar ? "VAR" : null;
    }
    if (adres == "")
        return null;
    return adres + " " + (cevap.ilAdi || "") + " / " + (cevap.ilceAdi || "");
}

/** UyapRequest'in errorCode'lu UYAP cevabıyla reddettiği nesnedeki kod. */
export function uyapHataKodu(e: unknown): string | undefined {
    if (e != null && typeof e === "object") {
        const kod = (e as { errorCode?: unknown }).errorCode;
        if (typeof kod === "string" && kod.length > 0)
            return kod;
    }
    return undefined;
}

export function uyapHataMesaji(e: unknown): string {
    if (e != null && typeof e === "object") {
        const o = e as { message?: unknown, error?: unknown };
        if (typeof o.message === "string" && o.message.length > 0)
            return o.message;
        if (typeof o.error === "string" && o.error.length > 0)
            return o.error;
    }
    return String(e);
}

/**
 * İşi durdurması gereken hatalar: kullanıcının Durdur'a basması, oturumun geri
 * gelmemesi, devre kesici, oturum kotası, kimlik uyuşmazlığı ve UYAP engeli (DEV-3572: UYAP_ENGEL cevaptan,
 * UYAP_ENGEL_KAPISI yerel ret; kodu kaybolmuş sarmalı hata için mesajdaki engel metni de sayılır). Bunlarda "eski davranışla
 * devam et" türünden bir geri dönüş yapılmaz. Yapılırsa durdurulan bir dosya sonraki
 * istekte oturum kurtarılınca imzalanıp ödenebilir.
 */
export function durdurucuHataMi(e: unknown): boolean {
    const mesaj = uyapHataMesaji(e);
    return mesaj === DEVRE_KESICI_MESAJI ||
        mesaj === KIMLIK_UYUSMAZLIGI_MESAJI ||
        mesaj === UYAP_ENGEL_KAPISI_MESAJI ||
        uyapEngelHatasiMi(e) ||
        uyapEngelMetniMi(mesaj) ||
        mesaj.indexOf("işlem durduruldu") >= 0 ||
        mesaj.indexOf("yeniden bağlanılamadı") >= 0 ||
        kotaDoluHatasiMi(e);
}

/** Portalın her tebligat talebine eklediği ortak alanlar. */
export const TEBLIGAT_TEMEL_ALANLARI: TebligatTalepAlani[] = [
    {id: "tebligatTuruAciklama", title: "Tebligat Türü"},
    {id: "adresTuruAciklama", title: "Adres Türü"},
    {id: "adres", title: "Adres"}
];
